import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Text,
  Stack,
  Title,
  Group,
  Card,
  Grid,
  List as MantineList,
  ThemeIcon,
  LoadingOverlay,
} from '@mantine/core';
import {
  type ApplicantStatistics,
  type ApplicantPersonalStatistics,
} from '@/schemas/API/POST/statistics/applicants/response';
import { PieChart, BarChart } from '@mantine/charts';
import { List } from '../List/List';
import { TextHeader } from '../List/Header/TextHeader';
import { colors } from '@/utils/colors';
import { type PagingOptions, useStatistics} from '@/hooks/statistics/useStatistics';
import type { Filter } from '@/schemas/API/POST/statistics/request';
import { SortHeader } from '../List/Header/SortHeader';
import { NoDataStatistics } from './NoDataStatistics';

interface Props {
  filter: Filter;
}

const genderMap = new Map<string, string>([
  ['FEMALE', 'žena'],
  ['MALE', 'muž'],
  ['OTHER', 'jiné'],
]);

const states = new Map<string, string>([
  ['CANCELLED', 'Storno'],
  ['ACTIVE', 'Aktivní'],
  ['INACTIVE', 'Neaktivní'],
  ['QUEUED', 'V pořadníku'],
  ['WAITING', 'Čekající'],
]);

const defaultApplicantStatistics: ApplicantStatistics = {
  applicants: {
    content: [],
    pageable: {
      paged: true,
      pageNumber: 0,
      pageSize: 10,
      offset: '0',
      unpaged: false,
      sort: {
        empty: true,
        sorted: false,
        unsorted: true,
      },
    },
    empty: 0,
    first: true,
    last: true,
    number: 0,
    numberOfElements: 0,
    size: 20,
    sort: {
      empty: true,
      sorted: false,
      unsorted: true,
    },
    totalElements: 0,
    totalPages: 0,
  },
  ages: [],
  genders: [],
  states: [],
};

const ApplicantNameCell = (applicant: ApplicantPersonalStatistics) => (
  <Text key={applicant.id}>{applicant.name}</Text>
);

const ApplicantEmailCell = (applicant: ApplicantPersonalStatistics) => (
    <Text key={applicant.id}>{applicant.email}</Text>
);

const ApplicantStateCell = (applicant: ApplicantPersonalStatistics) => (
  <Text key={applicant.id}>{states.get(applicant.state)}</Text>
);

export const ApplicantStatisticsComponent = ({
  filter,
}: Props): JSX.Element => {
  const navigate = useNavigate();

  const [requestParams, setRequestParams] = useState<PagingOptions>({
    page: 0,
    size: 10,
    sort: [],
  });

  const {
    data = defaultApplicantStatistics,
    error,
    isLoading,
  } = useStatistics<ApplicantStatistics>('applicants', filter, requestParams);

  
  /*  Handle special cases */ 
  if (data.applicants.empty && !isLoading) { // needs to be executed before the others, otherwise permanent loading
    return <NoDataStatistics />;
  }

  if (error) {
    return <Text>Chyba při načítání dat.</Text>;
  }

  const onPageChange = (pageNumber: number) => {
    setRequestParams({
      ...requestParams,
      page: pageNumber,
    });
  };

  const maxAgeAmount = Math.max(...data.ages.map(a => a.amount));
  let ticks: number[] = [];

  if (maxAgeAmount <= 5) {
    ticks = Array.from({ length: maxAgeAmount + 1 }, (_, i) => i);
  } else {
    const step = Math.ceil(maxAgeAmount / 5);
    ticks = Array.from(
      { length: Math.floor(maxAgeAmount / step) + 1 },
      (_, i) => i * step
    );

    if (ticks[ticks.length - 1] !== maxAgeAmount) {
      ticks.push(step * 5);
    }
  }

  const domain: [number, number] = [0, ticks[ticks.length - 1]];

  return (
    <Stack gap="lg" align="flex-start">
      <LoadingOverlay
        visible={isLoading}
        zIndex={1000}
        overlayProps={{ radius: 'sm', blur: 2 }}
        loaderProps={{ color: 'blue', type: 'bars' }}
      />
      <Grid grow justify="center" align="stretch">
        <Grid.Col span={4}>
          <Card h="100%" shadow="sm" padding="md" radius="md" withBorder>
            <Group
              justify="space-evenly"
              className="w-full h-full rounded-card"
            >
              <Stack align="center" justify="flex-start">
                <Title order={5}>Stav</Title>

                <PieChart
                  data={data.states.map((item, index) => ({
                    name: states.get(item.name) ?? item.name,
                    value: item.amount,
                    color: colors[index % colors.length],
                  }))}
                  tooltipDataSource="segment"
                  withTooltip
                  tooltipAnimationDuration={200}
                  withLabelsLine
                  labelsPosition="outside"
                  strokeWidth={2}
                  size={200}
                />
              </Stack>
              <MantineList spacing="xs" size="sm" withPadding>
                {data.states.map((item, index) => (
                  <MantineList.Item
                    key={item.name}
                    icon={
                      <ThemeIcon
                        color={colors[index % colors.length]}
                        radius="xl"
                        size={10}
                      />
                    }
                  >
                    {states.get(item.name) ?? item.name} ({item.amount} osob)
                  </MantineList.Item>
                ))}
              </MantineList>
            </Group>
          </Card>
        </Grid.Col>

        <Grid.Col span={6}>
          <Card h="100%" shadow="sm" padding="md" radius="md" withBorder>
            <Group justify="space-evenly" className="w-full">
              <Stack align="center" justify="flex-start">
                <Title order={5}>Rozdělení podle pohlaví</Title>
                <PieChart
                  data={data.genders.map((item, index) => ({
                    name: genderMap.get(item.name) ?? item.name,
                    value: item.amount,
                    color: colors[index % colors.length],
                  }))}
                  tooltipDataSource="segment"
                  withTooltip
                  tooltipAnimationDuration={200}
                  size={200}
                />
              </Stack>
              <MantineList spacing="xs" size="sm" withPadding>
                {data.genders.map((item, index) => (
                  <MantineList.Item
                    key={item.name}
                    icon={
                      <ThemeIcon
                        color={colors[index % colors.length]}
                        radius="xl"
                        size={10}
                      />
                    }
                  >
                    {genderMap.get(item.name) ?? item.name} ({item.amount} osob)
                  </MantineList.Item>
                ))}
              </MantineList>
            </Group>
          </Card>
        </Grid.Col>
        <Grid.Col span={4}>
          <Card h="100%" shadow="sm" padding="md" radius="md" withBorder>
            <Stack justify="center">
              <Title order={5}>Rozdělení podle věku</Title>
              <BarChart
                h={200}
                data={data.ages.map(a => ({
                  name: a.name,
                  počet: a.amount,
                }))}
                dataKey="name"
                series={[{ name: 'počet', color: 'blue.6' }]}
                xAxisProps={{
                  domain,
                  ticks,
                }}
                tooltipAnimationDuration={200}
                orientation="vertical"
              />
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>

      <div className="h-full w-full overflow-auto">
        <List
          loading={!data}
          pagination={{
            page: Number(data.applicants.pageable.pageNumber + 1),
            setPage: page => onPageChange(page),
            pageTotal: data.applicants.totalPages ?? 1,
          }}
          perPageSelector={{
            label: 'Uchazečů na stránce',
            value: requestParams.size ?? 0,
            setter: v =>
              setRequestParams(currentParams => ({
                ...currentParams,
                size: v,
              })),
          }}
          columns={[
            <SortHeader
              label="Jméno"
              key="jmeno"
              fields={['name']}
              callback={sort => {
                const newSort = sort.length ? sort : ['name,asc'];
                setRequestParams(currentParams => ({
                  ...currentParams,
                  sort: newSort,
                }));
              }}
              currentSort={requestParams.sort ?? []}
            />,
            <TextHeader key="email">E-mail</TextHeader>,
            <TextHeader key="role">Stav</TextHeader>,
          ]}
          content={{
            items: data?.applicants.content ?? [],
            onClick: (applicant: ApplicantPersonalStatistics) =>
              navigate(`/applicants/${applicant.id}`),
            renders: [
              ApplicantNameCell,
              ApplicantEmailCell,
              ApplicantStateCell,
            ],
          }}
        />
      </div>
    </Stack>
  );
};
